/**
 * quizRequests.ts
 * -----------------
 * This file contains the requests needed to perform
 * quiz creation, get quizzes, among others.
 */
import { PresignedPost, QuizListType } from "utils/interfaces";
import { createRecordRequest, updateRecordRequest } from "./apiRequests";
import { sendFilesToS3ByPresignedPost } from "./filesRequests";

/**
 * Function used to create and update quizzes in the context of
 * creating/editing a course. This creates the quizzes if they
 * are being created inside a "Create New Course", and update
 * which quiz is active inside "Editing Course".
 * @param courseId _id of the course that contains these quizzes.
 * @param quizzes List of quizzes to be created/updated.
 * @param baseSelectedQuizzes In the "Editing course" context, the quizzes that were selected originally.
 * @param platform Platform obtained from the `usePlatform()` hook.
 */
export const createUpdateQuizzesInCourses = async (
  courseId: string,
  quizzes: QuizListType[],
  baseSelectedQuizzes: { [key: string]: boolean },
  platform: string,
) => {
  // Check if the critical parameters are defined
  if (!(platform && courseId)) return;

  // Get all the new quizzes to be created and send them to be created
  const newQuizPromises = quizzes
    .filter((quiz: QuizListType) => quiz.tempId)
    .map(async (quiz: QuizListType) => {
      // Get the quiz data
      const { _id, badgeImage, ...quizData } = quiz;

      // Add the course _id to the quiz
      quizData.course = courseId;

      // Send the request to be created
      const quizResponse: {
        _id: string;
        successful: boolean;
        message: string;
        badgeImageUrl?: PresignedPost;
      } = await createRecordRequest(quizData, "/api/quizzes");

      // If the response is successful, then show the pop up message
      if (!quizResponse.successful) {
        console.log(quizResponse.message);
        return;
      }

      // Upload the logo
      if (badgeImage) {
        await sendFilesToS3ByPresignedPost(
          badgeImage,
          `${platform}/quizzes/${quizResponse._id}/badgeImage`,
          quizResponse.badgeImageUrl,
        );
      }
    });

  // And for the currently created ones, check if the activate has changed
  const updateQuizPromises = quizzes
    .filter((quiz: QuizListType) => quiz._id)
    .map(async (quiz: QuizListType) => {
      // Compare if the activate field has changed
      if (quiz.active !== baseSelectedQuizzes[quiz._id]) {
        // Then update the quiz activate status
        const quizResponse = await updateRecordRequest(
          { _id: quiz._id, toUpdate: { active: quiz.active } },
          "/api/quizzes",
        );

        // If the response is successful, then show the pop up message
        if (!quizResponse.successful) {
          console.log(quizResponse.message);
        }
      }
    });

  // Wait for all promises to resolve
  await Promise.all([...newQuizPromises, ...updateQuizPromises]);
};
